import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ComponentProps,
  type FocusEvent,
} from 'react';
import { Input } from '@/components/atoms/input';

/**
 * A number field you can actually empty.
 *
 * The usual `onChange={(e) => setValue(Number(e.target.value))}` makes the last
 * digit undeletable: clearing the box sends `Number('')` — 0 — back down as the
 * value, and the controlled input immediately re-renders "0". To replace 42 with
 * 7 you have to select-all first; backspacing leaves you fighting a 0 that keeps
 * reappearing.
 *
 * The fix is to keep the typed text here rather than re-deriving it from the
 * number on every render. An empty box stays empty, and the parent is told what
 * empty means for it via `emptyValue` — `null` for a field that is genuinely
 * optional, `0` (or a floor of 1) for state that is typed as a plain number.
 *
 * Use `LiveNumberInput` instead where a field feeds figures that must track it
 * keystroke by keystroke (a line subtotal, a document total); it clamps and
 * commits on every keypress, which this deliberately does not do.
 */
export interface NumberInputProps extends Omit<
  ComponentProps<typeof Input>,
  'type' | 'value' | 'onChange'
> {
  value: number | null | undefined;
  /** Called with the parsed number, or with `emptyValue` when the box is cleared. */
  onChange: (value: number | null) => void;
  /** What an empty box means to the parent. Defaults to `null`. */
  emptyValue?: number | null;
  /** Whole numbers only — a count of days, a sequence number, a quantity. */
  allowDecimal?: boolean;
}

/**
 * Read a `NumberInput` value out of loosely-typed state — a settings draft held
 * as `Record<string, unknown>`, a query param, a form value of unknown shape.
 * Anything that is not a finite number reads as blank.
 */
export function toNumberValue(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function format(value: number | null | undefined): string {
  return value === null || value === undefined || Number.isNaN(value) ? '' : String(value);
}

/** The number the parent would be holding if it had accepted this text. */
function parseDraft(draft: string, emptyValue: number | null): number | null {
  if (draft.trim() === '') return emptyValue;
  const parsed = Number(draft);
  return Number.isNaN(parsed) ? emptyValue : parsed;
}

interface NumberFieldOptions {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  emptyValue?: number | null;
  /** Whole numbers only — a count of days, a sequence number, a quantity. */
  allowDecimal?: boolean;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
}

/**
 * The typed text is authoritative only while the field has focus — that window
 * is the whole point, since it is what lets the box sit empty between deleting
 * the old figure and typing the new one. The moment focus leaves, the box goes
 * back to showing what the parent actually holds.
 *
 * That boundary is deliberate: a box left blank while the state behind it reads
 * 1, 30, or 0 is a field lying about its own value, and no amount of tracking
 * *why* it is blank makes it honest.
 */
function useDraftText(value: number | null | undefined) {
  const [draft, setDraft] = useState(() => format(value));
  const focusedRef = useRef(false);

  useEffect(() => {
    if (focusedRef.current) return;
    setDraft(format(value));
  }, [value]);

  return { draft, setDraft, focusedRef };
}

/** Truncate rather than round, so a field reads what `parseInt` used to give. */
function commitValue(parsed: number | null, allowDecimal: boolean): number | null {
  if (allowDecimal || parsed === null) return parsed;
  return Math.trunc(parsed);
}

/**
 * The clearable-number behaviour on its own, for the handful of fields that
 * render a bare `<input>` rather than the `Input` atom (the Iris studio's own
 * field chrome, for one). Spread the result onto the element.
 */
export function useNumberField({
  value,
  onChange,
  emptyValue = null,
  allowDecimal = true,
  onFocus,
  onBlur,
}: NumberFieldOptions) {
  const { draft, setDraft, focusedRef } = useDraftText(value);

  return {
    type: 'number' as const,
    value: draft,
    onChange(event: ChangeEvent<HTMLInputElement>) {
      const raw = event.target.value;
      setDraft(raw);
      // A number input reports "" for text it cannot parse — "1e" on the way to
      // "1e3", or a lone "-" — and flags it `badInput`. That is not a cleared
      // field, so hold the last committed value rather than reading it as empty.
      if (event.target.validity?.badInput) return;
      onChange(commitValue(parseDraft(raw, emptyValue), allowDecimal));
    },
    onFocus(event: FocusEvent<HTMLInputElement>) {
      focusedRef.current = true;
      onFocus?.(event);
    },
    onBlur(event: FocusEvent<HTMLInputElement>) {
      focusedRef.current = false;
      // Leaving settles the box on what the parent actually took: "0012" tidies
      // to "12", a clamped or truncated figure stops disagreeing with the field,
      // and a box cleared on the way to a new number that never arrived shows
      // the value it still holds rather than a misleading blank.
      setDraft(format(value));
      onBlur?.(event);
    },
  };
}

/**
 * The same behaviour for a text box that happens to hold a number — the
 * `inputMode="decimal"` money fields that want no spinners and no locale-parsed
 * `type="number"` quirks. Characters outside the numeric set are dropped as they
 * are typed, and the draft keeps a trailing "." so cents can be entered at all:
 * re-deriving the text from `Number("0.")` collapsed it back to "0" and made
 * "0.5" impossible to type.
 */
export function useNumericTextField({
  value,
  onChange,
  emptyValue = null,
  allowDecimal = true,
  onFocus,
  onBlur,
}: NumberFieldOptions) {
  const { draft, setDraft, focusedRef } = useDraftText(value);

  function sanitize(raw: string): string {
    if (!allowDecimal) return raw.replace(/[^0-9]/g, '');
    const cleaned = raw.replace(/[^0-9.]/g, '');
    const parts = cleaned.split('.');
    // Fold any extra dots into the fractional part rather than dropping the
    // keystroke, and read a leading "." as "0." so the box never sits on a bare
    // dot that looks filled and parses to nothing.
    const single = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : cleaned;
    return single.startsWith('.') ? `0${single}` : single;
  }

  return {
    type: 'text' as const,
    inputMode: (allowDecimal ? 'decimal' : 'numeric') as 'decimal' | 'numeric',
    value: draft,
    onChange(event: ChangeEvent<HTMLInputElement>) {
      const next = sanitize(event.target.value);
      setDraft(next);
      onChange(parseDraft(next, emptyValue));
    },
    onFocus(event: FocusEvent<HTMLInputElement>) {
      focusedRef.current = true;
      onFocus?.(event);
    },
    onBlur(event: FocusEvent<HTMLInputElement>) {
      focusedRef.current = false;
      setDraft(format(value));
      onBlur?.(event);
    },
  };
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput(
  { value, onChange, emptyValue = null, allowDecimal = true, onFocus, onBlur, ...props },
  ref,
) {
  const field = useNumberField({ value, onChange, emptyValue, allowDecimal, onFocus, onBlur });
  return <Input {...props} {...field} ref={ref} />;
});

export interface NumericTextInputProps extends Omit<
  ComponentProps<typeof Input>,
  'type' | 'inputMode' | 'value' | 'onChange'
> {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  emptyValue?: number | null;
  /** Whole numbers only — a count of months, a number of days. */
  allowDecimal?: boolean;
}

/** `NumberInput` without the spinners: a plain text box that holds a number. */
export const NumericTextInput = forwardRef<HTMLInputElement, NumericTextInputProps>(
  function NumericTextInput(
    { value, onChange, emptyValue = null, allowDecimal = true, onFocus, onBlur, ...props },
    ref,
  ) {
    const field = useNumericTextField({
      value,
      onChange,
      emptyValue,
      allowDecimal,
      onFocus,
      onBlur,
    });
    return <Input {...props} {...field} ref={ref} />;
  },
);
